import type { CardData, DataAdapter, PullOptions } from '@gachakit/core/types/adapter';
import { computeRarity } from './rarity-rules';

const GBIF_API = 'https://api.gbif.org/v1';
const RANDOM_OCCURRENCE_WINDOW = 99980;
const PREFETCH_BATCH_SIZE = 24;

interface GBIFOccurrenceSearchResponse {
  results?: GBIFOccurrence[];
}

interface GBIFOccurrence {
  key: number;
  taxonKey?: number;
  speciesKey?: number;
  scientificName?: string;
  kingdom?: string;
  phylum?: string;
  class?: string;
  order?: string;
  family?: string;
  genus?: string;
  species?: string;
  habitat?: string;
  issue?: string;
  media?: GBIFMedia[];
}

interface GBIFMedia {
  type?: string;
  format?: string;
  identifier?: string;
}

export class GBIFAdapter implements DataAdapter {
  id = 'gbif-species';
  name = 'Species of Earth';
  description = 'Discover creatures from GBIF through a mobile-first gacha experience.';

  private candidateQueue: CardData[] = [];
  private pendingPrefetch: Promise<void> | null = null;

  async pullRandom(options?: PullOptions): Promise<CardData> {
    for (let attempt = 0; attempt < 6; attempt += 1) {
      await this.ensureCandidatePool(18);
      const matchIndex = this.candidateQueue.findIndex((candidate) => matchesPullOptions(candidate, options));
      if (matchIndex >= 0) {
        const [card] = this.candidateQueue.splice(matchIndex, 1);
        void this.prefetch(12);
        return card;
      }

      await this.prefetch(PREFETCH_BATCH_SIZE);
    }

    throw new Error('NO_MATCHING_CARD');
  }

  async pullById(id: string): Promise<CardData> {
    const occurrences = await this.fetchOccurrences(
      new URLSearchParams({
        mediaType: 'StillImage',
        taxonKey: id,
        limit: '16'
      })
    );

    const card = occurrences
      .map((occurrence) => this.mapOccurrenceToCard(occurrence))
      .find((candidate): candidate is CardData => Boolean(candidate));

    if (!card) {
      throw new Error('CARD_NOT_FOUND');
    }

    return card;
  }

  async search(query: string): Promise<CardData[]> {
    const occurrences = await this.fetchOccurrences(
      new URLSearchParams({
        q: query,
        mediaType: 'StillImage',
        limit: '18'
      })
    );

    const cards = occurrences
      .map((occurrence) => this.mapOccurrenceToCard(occurrence))
      .filter((candidate): candidate is CardData => Boolean(candidate));

    const uniqueCards = new Map<string, CardData>();
    for (const card of cards) {
      if (!uniqueCards.has(card.id)) {
        uniqueCards.set(card.id, card);
      }
    }

    return Array.from(uniqueCards.values()).slice(0, 10);
  }

  async prefetch(count = PREFETCH_BATCH_SIZE): Promise<void> {
    if (this.pendingPrefetch) {
      await this.pendingPrefetch;
      return;
    }

    const task = this.fillQueue(count);
    this.pendingPrefetch = task;

    try {
      await task;
    } finally {
      this.pendingPrefetch = null;
    }
  }

  private async ensureCandidatePool(minimum: number): Promise<void> {
    if (this.candidateQueue.length >= minimum) {
      return;
    }

    await this.prefetch(Math.max(PREFETCH_BATCH_SIZE, minimum - this.candidateQueue.length));
  }

  private async fillQueue(targetCount: number): Promise<void> {
    let safety = 0;
    while (this.candidateQueue.length < targetCount && safety < 4) {
      const offset = Math.floor(Math.random() * RANDOM_OCCURRENCE_WINDOW);
      const occurrences = await this.fetchOccurrences(
        new URLSearchParams({
          mediaType: 'StillImage',
          limit: String(PREFETCH_BATCH_SIZE),
          offset: String(offset)
        })
      );

      for (const occurrence of occurrences) {
        const card = this.mapOccurrenceToCard(occurrence);
        if (!card) {
          continue;
        }

        if (this.candidateQueue.some((candidate) => candidate.id === card.id)) {
          continue;
        }

        this.candidateQueue.push(card);
      }

      safety += 1;
    }
  }

  private async fetchOccurrences(params: URLSearchParams): Promise<GBIFOccurrence[]> {
    const response = await fetch(`${GBIF_API}/occurrence/search?${params.toString()}`);
    if (!response.ok) {
      return [];
    }

    const payload = (await response.json()) as GBIFOccurrenceSearchResponse;
    return (payload.results ?? []).filter((occurrence) => Boolean(pickImage(occurrence.media)));
  }

  private mapOccurrenceToCard(occurrence: GBIFOccurrence): CardData | null {
    const speciesId = String(occurrence.speciesKey ?? occurrence.taxonKey ?? '');
    if (!speciesId) {
      return null;
    }

    const imageUrl = pickImage(occurrence.media);
    if (!imageUrl) {
      return null;
    }

    const canonicalName = occurrence.species ?? occurrence.scientificName ?? 'Unknown species';
    const title = occurrence.species ?? occurrence.scientificName ?? canonicalName;
    const taxonomy = [occurrence.phylum, occurrence.order, occurrence.family].filter(Boolean).join(' / ');
    const habitat = occurrence.habitat ?? 'Various';

    return {
      id: speciesId,
      name: title,
      subtitle: canonicalName,
      description: taxonomy || 'Taxonomy data is sparse for this specimen.',
      imageUrl,
      rarity: computeRarity({ id: speciesId }),
      tags: [occurrence.kingdom, occurrence.phylum, occurrence.class, occurrence.order]
        .filter((value): value is string => Boolean(value))
        .map((value) => value.toLowerCase()),
      stats: [
        { label: 'Kingdom', value: occurrence.kingdom ?? 'Unknown' },
        { label: 'Class', value: occurrence.class ?? 'Unknown' },
        {
          label: 'Rarity',
          value: computeRarity({ id: speciesId }).toUpperCase(),
          highlight: true
        },
        { label: 'Habitat', value: habitat }
      ],
      sourceUrl: `https://www.gbif.org/occurrence/${occurrence.key}`,
      metadata: {
        occurrence
      }
    };
  }
}

function matchesPullOptions(card: CardData, options: PullOptions | undefined): boolean {
  if (options?.excludeIds?.includes(card.id)) {
    return false;
  }

  if (options?.tags?.length && !options.tags.every((tag) => card.tags.includes(tag.toLowerCase()))) {
    return false;
  }

  if (options?.guaranteeRarity && card.rarity !== options.guaranteeRarity) {
    return false;
  }

  return true;
}

function pickImage(media: GBIFMedia[] | undefined): string | undefined {
  if (!media?.length) {
    return undefined;
  }

  return media.find((item) => item.type === 'StillImage' && item.identifier)?.identifier;
}
